import { useEffect, useState } from "react";
import { Tags, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ICON_MAP, ICON_CHOICES, COLOR_CHOICES, getCatMeta } from "../lib/categoryMeta";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

function CategorySection({ title, type, cats, onChanged, onEditMeta, canAdd, canDelete }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const defaults = cats[type] .filter((c) => !cats[`custom_${type}`].includes(c));

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/categories", { type, name: name.trim() });
      toast.success(`"${name.trim()}" kategorisi eklendi`);
      setName("");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cat) => {
    try {
      await api.delete(`/categories?type=${type}&name=${encodeURIComponent(cat)}`);
      toast.success(`"${cat}" silindi`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3">
      <p className={`text-sm font-semibold ${type === "income" ? "text-emerald-400" : "text-red-400"}`}>{title}</p>
      <div className="flex flex-wrap gap-2">
        {defaults.map((c) => {
          const { Icon, color } = getCatMeta(cats.meta, c, type);
          return (
            <button key={c} data-testid={`category-chip-${type}-${c}`} onClick={() => onEditMeta(c, type)}
              title="İkon ve renk seç"
              className="flex items-center gap-1.5 text-xs bg-secondary/60 text-muted-foreground rounded-full px-3 py-1.5 hover:bg-secondary transition-colors">
              <Icon className="w-3.5 h-3.5" style={{ color }} />
              {c}
            </button>
          );
        })}
        {cats[`custom_${type}`].map((c) => {
          const { Icon, color } = getCatMeta(cats.meta, c, type);
          return (
            <span key={c} data-testid={`custom-category-${type}-${c}`}
              className="flex items-center gap-1.5 text-xs bg-primary/15 text-primary rounded-full pl-3 pr-1.5 py-1">
              <button data-testid={`category-chip-${type}-${c}`} onClick={() => onEditMeta(c, type)}
                title="İkon ve renk seç" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
                <Icon className="w-3.5 h-3.5" style={{ color }} />
                {c}
              </button>
              {canDelete && (
              <button data-testid={`delete-category-${type}-${c}`} onClick={() => remove(c)}
                className="p-0.5 rounded-full hover:bg-primary/25 transition-colors">
                <X className="w-3 h-3" />
              </button>
              )}
            </span>
          );
        })}
      </div>
      <div className="flex gap-2">
        {canAdd && (
        <><Input data-testid={`add-category-input-${type}`} placeholder="Yeni kategori adı" maxLength={40}
          value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} className="max-w-xs" />
        <Button data-testid={`add-category-button-${type}`} variant="outline" onClick={add} disabled={busy || !name.trim()}
          className="rounded-full border-border hover:bg-secondary shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ekle
        </Button></>
        )}
      </div>
    </div>
  );
}

export default function CategoryManager() {
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const isOwner = user?.role === "owner";
  const [cats, setCats] = useState(null);
  const [metaEdit, setMetaEdit] = useState(null); // {name, type, icon, color}
  const [savingMeta, setSavingMeta] = useState(false);

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  if (!cats) return null;

  const openMetaEditor = (name, type) => {
    const m = cats.meta?.[name] || {};
    setMetaEdit({
      name, type,
      icon: ICON_MAP[m.icon] ? m.icon : (type === "income" ? "trending-up" : "trending-down"),
      color: m.color || (type === "income" ? "#10B981" : "#EF4444"),
    });
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const { data } = await api.put("/categories/meta", { name: metaEdit.name, icon: metaEdit.icon, color: metaEdit.color });
      setCats(data);
      toast.success(`"${metaEdit.name}" görünümü güncellendi`);
      setMetaEdit(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingMeta(false);
    }
  };

  const PreviewIcon = metaEdit ? ICON_MAP[metaEdit.icon] : null;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-6" data-testid="category-manager">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
          <Tags className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold">Kategoriler</h2>
          <p className="text-xs text-muted-foreground">Kendi kategorilerinizi ekleyin; ikon ve renk seçmek için kategoriye tıklayın</p>
        </div>
      </div>
      <CategorySection title="Gelir Kategorileri" type="income" cats={cats} onChanged={load} onEditMeta={openMetaEditor} canAdd={!!perms.add} canDelete={isOwner} />
      <CategorySection title="Gider Kategorileri" type="expense" cats={cats} onChanged={load} onEditMeta={openMetaEditor} canAdd={!!perms.add} canDelete={isOwner} />

      <Dialog open={!!metaEdit} onOpenChange={(o) => !o && setMetaEdit(null)}>
        <DialogContent data-testid="category-meta-dialog" className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {PreviewIcon && (
                <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${metaEdit?.color}26` }}>
                  <PreviewIcon className="w-4 h-4" style={{ color: metaEdit?.color }} />
                </span>
              )}
              {metaEdit?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">Kategori için ikon ve renk seçin</DialogDescription>
          </DialogHeader>
          {metaEdit && (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-medium mb-2">İkon</p>
                <div className="grid grid-cols-8 gap-1.5">
                  {ICON_CHOICES.map((name) => {
                    const Ic = ICON_MAP[name];
                    const selected = metaEdit.icon === name;
                    return (
                      <button key={name} data-testid={`icon-choice-${name}`}
                        onClick={() => setMetaEdit({ ...metaEdit, icon: name })}
                        className={`aspect-square rounded-lg flex items-center justify-center border transition-colors ${
                          selected ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary"
                        }`}>
                        <Ic className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Renk</p>
                <div className="flex flex-wrap gap-2">
                  {COLOR_CHOICES.map((c) => (
                    <button key={c} data-testid={`color-choice-${c.slice(1)}`}
                      onClick={() => setMetaEdit({ ...metaEdit, color: c })}
                      className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                        metaEdit.color === c ? "ring-2 ring-offset-2 ring-offset-card ring-foreground" : ""
                      }`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <Button data-testid="category-meta-save" onClick={saveMeta} disabled={savingMeta}
                className="w-full rounded-full font-semibold">
                {savingMeta && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Kaydet
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
